import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { dashboardAPI as API } from '../../../axiosConfig';

const URL = 'proxy?endpoint=supernova/dynamic/rule';

const SLICE_NAME = 'paymentOrchestrationDynamicRules';
const CREATE_PAYMENT_ORCHESTRATION_DYNAMIC_RULE = `${SLICE_NAME}/dynamic-rules/create`;
const UPDATE_PAYMENT_ORCHESTRATION_DYNAMIC_RULE = `${SLICE_NAME}/dynamic-rules/update`;
const DELETE_PAYMENT_ORCHESTRATION_DYNAMIC_RULE = `${SLICE_NAME}/dynamic-rules/delete`;
const LIST_PAYMENT_ORCHESTRATION_DYNAMIC_RULE = `${SLICE_NAME}/dynamic-rules/list`;
const DETAILS_PAYMENT_ORCHESTRATION_DYNAMIC_RULE = `${SLICE_NAME}/dynamic-rules/details`;

export const createDynamicRule = createAsyncThunk(
  CREATE_PAYMENT_ORCHESTRATION_DYNAMIC_RULE,
  async (payload) => {
    const response = await API.post(URL, payload).catch((error) => {
      console.error(JSON.stringify(error));
      return error && error.response;
    });

    return response.data;
  },
);

export const updateDynamicRule = createAsyncThunk(
  UPDATE_PAYMENT_ORCHESTRATION_DYNAMIC_RULE,
  async ({ id, ...payload }) => {
    const url = `${URL}/${id}`;

    const response = await API.put(url, payload).catch((error) => {
      console.error(JSON.stringify(error));
      return error && error.response;
    });

    return response.data;
  },
);

export const deleteDynamicRule = createAsyncThunk(
  DELETE_PAYMENT_ORCHESTRATION_DYNAMIC_RULE,
  async ({ id }) => {
    const url = `${URL}/${id}`;

    const response = await API.delete(url).catch((error) => {
      console.error(JSON.stringify(error));
      return error && error.response;
    });

    return response.data;
  },
);

export const getDynamicRuleList = createAsyncThunk(
  LIST_PAYMENT_ORCHESTRATION_DYNAMIC_RULE,
  async (_, { rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await API.get(URL);

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getSelectedDynamicRuleDetails = createAsyncThunk(
  DETAILS_PAYMENT_ORCHESTRATION_DYNAMIC_RULE,
  async (id, { rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await API.get(`${URL}/${id}`);

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const initialState = {
  dynamicRuleList: [],
  dynamicRuleListStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed',

  dynamicRuleCreateStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed',

  dynamicRuleUpdateStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed',

  selectedDynamicRuleDetailsStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed',
  selectedDynamicRuleDetails: null,
};

const paymentOrchestrationDynamicRulesSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    resetSelectedDynamicRule(state) {
      state.selectedDynamicRuleDetails = null;
      state.selectedDynamicRuleDetailsStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDynamicRuleList.pending, (state) => {
        state.dynamicRuleListStatus = 'loading';
      })
      .addCase(getDynamicRuleList.rejected, (state) => {
        state.dynamicRuleList = [];
        state.dynamicRuleListStatus = 'failed';
      })
      .addCase(getDynamicRuleList.fulfilled, (state, { payload }) => {
        state.dynamicRuleList = payload;
        state.dynamicRuleListStatus = 'succeeded';
      });

    builder
      .addCase(createDynamicRule.pending, (state) => {
        state.dynamicRuleCreateStatus = 'loading';
      })
      .addCase(createDynamicRule.rejected, (state) => {
        state.dynamicRuleCreateStatus = 'failed';
      })
      .addCase(createDynamicRule.fulfilled, (state) => {
        state.dynamicRuleCreateStatus = 'succeeded';
      });

    builder
      .addCase(updateDynamicRule.pending, (state) => {
        state.dynamicRuleUpdateStatus = 'loading';
      })
      .addCase(updateDynamicRule.rejected, (state) => {
        state.dynamicRuleUpdateStatus = 'failed';
      })
      .addCase(updateDynamicRule.fulfilled, (state) => {
        state.dynamicRuleUpdateStatus = 'succeeded';
      });

    builder
      .addCase(getSelectedDynamicRuleDetails.pending, (state) => {
        state.selectedDynamicRuleDetailsStatus = 'loading';
      })
      .addCase(getSelectedDynamicRuleDetails.rejected, (state) => {
        state.selectedDynamicRuleDetails = null;
        state.selectedDynamicRuleDetailsStatus = 'failed';
      })
      .addCase(
        getSelectedDynamicRuleDetails.fulfilled,
        (state, { payload }) => {
          state.selectedDynamicRuleDetails = payload;
          state.selectedDynamicRuleDetailsStatus = 'succeeded';
        },
      );
  },
});

export const paymentOrchestrationRulesActions =
  paymentOrchestrationDynamicRulesSlice.actions;

export const selectDynamicRuleList = (state) =>
  state.paymentOrchestration.dynamicRules.dynamicRuleList;

export const selectIsLoadingDynamicRuleList = (state) =>
  state.paymentOrchestration.dynamicRules.dynamicRuleListStatus === 'loading';

export const selectIsCreatingDynamicRule = (state) =>
  state.paymentOrchestration.dynamicRules.dynamicRuleCreateStatus === 'loading';

export const selectIsUpdatingDynamicRule = (state) =>
  state.paymentOrchestration.dynamicRules.dynamicRuleUpdateStatus === 'loading';

export const selectIsLoadingSelectedDynamicRuleDetails = (state) =>
  state.paymentOrchestration.dynamicRules.selectedDynamicRuleDetailsStatus ===
  'loading';

export const selectSelectedDynamicRuleDetails = (state) =>
  state.paymentOrchestration.dynamicRules.selectedDynamicRuleDetails;

export default paymentOrchestrationDynamicRulesSlice.reducer;
